<script setup lang="ts">
type Props = {
  opacity?: boolean;
};

const { opacity = false } = defineProps<Props>();

interface SocialAccount {
  id?: number;
  social_type: 'facebook' | 'google' | 'twitch' | 'steam';
  name: string;
  logo: string;
  isAvailable: boolean;
  isConnected: boolean;
  connect?: () => void;
}

const route = useRoute();
const { ApiService } = useApiService();
const { getSecureQueryParam } = useRoutes();
const { $socialAuth } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();

const {
  isGoogleButtonActive,
  isFacebookButtonActive,
  isTwitchButtonActive,
  isSteamButtonActive,
} = useFeatureFlagsNew();

const isLoading = ref(false);
const isEditing = ref<boolean>(false);

const socialAccountsList = ref<SocialAccount[]>([
  {
    social_type: 'google',
    name: 'Google',
    logo: 'social/google-color',
    isAvailable: isGoogleButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'twitch',
    name: 'Twitch',
    logo: 'social/twitch-color',
    isAvailable: isTwitchButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'facebook',
    name: 'Facebook',
    logo: 'social/facebook-color',
    isAvailable: isFacebookButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'steam',
    name: 'Steam',
    logo: 'social/steam-color',
    isAvailable: isSteamButtonActive.value,
    isConnected: false,
  },
]);

const socialAccountsListConnected = computed(() => {
  return socialAccountsList.value.filter(
    (socialAccount) => socialAccount.isConnected,
  );
});

const connectedAccounts = computed(() => {
  return socialAccountsListConnected.value.length;
});

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

async function onConnectButtonClick(socialAccount: SocialAccount) {
  if (!socialAccount.social_type) {
    return;
  }

  const hasUserConfirmed = confirm(
    t('account.security.social_accounts.connect_confirmation'),
  );

  if (!hasUserConfirmed) {
    return;
  }

  isLoading.value = true;

  await ApiService.post(
    `bff/users/account-social/connect/${socialAccount.social_type}`,
    {},
  )
    .then(async ({ data }) => {
      await $socialAuth.provider(socialAccount.social_type).connect({
        connectParams: {
          token: data.token,
          ts: data.ts,
          hash: data.hash,
          salt: data.salt,
        },
      });
    })
    .catch((error) => {
      const message = error.message
        ? getSecureQueryParam(route.query.message || '')
        : t('general.error');

      openToaster({
        message,
        type: 'error',
      });

      isLoading.value = false;
    });
}

async function onDisconnectButtonClick(socialAccount: SocialAccount) {
  if (!socialAccount.id) {
    return;
  }

  const hasUserConfirmed = confirm(
    t('account.security.social_accounts.disconnect_confirmation'),
  );

  if (!hasUserConfirmed) {
    return;
  }

  isLoading.value = true;

  await ApiService.delete(`bff/users/account-social/${socialAccount.id}`).then(
    async () => {
      socialAccount.isConnected = false;

      await fetchSocialAccounts();
    },
  );

  isLoading.value = false;
}

const loadingSocialAccounts = ref<boolean>(false);

async function fetchSocialAccounts() {
  loadingSocialAccounts.value = true;

  const { data } = await ApiService.get('bff/users/account-social');

  if (!data || !data.length) {
    loadingSocialAccounts.value = false;

    return;
  }

  for (let i = 0; i < data.length; i++) {
    for (let j = 0; j < socialAccountsList.value.length; j++) {
      if (
        data[i]?.social_type &&
        socialAccountsList.value[j]?.social_type &&
        data[i]?.social_type !== socialAccountsList.value[j]?.social_type
      ) {
        continue;
      }

      socialAccountsList.value[j]!.isConnected = true;
      socialAccountsList.value[j]!.id = data[i]!.id;
    }
  }

  loadingSocialAccounts.value = false;
}

onMounted(async () => {
  await fetchSocialAccounts();

  if (route.query.link_account_social) {
    setTimeout(() => {
      const message = route.query.message
        ? getSecureQueryParam(route.query.message)
        : t('general.error');

      openToaster({
        message,
        type: 'success',
      });
    }, 500);
  }
});

onActivated(async () => {
  await fetchSocialAccounts();
});

const hasPendency = computed(() => false);

defineExpose({
  name: 'social',
  isEditing,
  hasPendency,
  toggleEdit,
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />
    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div :class="$style.header">
        <p :class="$style.title">
          {{ $t('account.security.social_accounts.title') }}
        </p>
        <button
          :class="$style.edit"
          @click="toggleEdit"
        >
          {{ isEditing ? $t('general.cancel') : $t('general.edit') }}
        </button>
      </div>
      <div
        v-if="!isEditing"
        :class="$style.viewContent"
      >
        <p
          v-if="loadingSocialAccounts"
          :class="$style.text"
        >
          {{ $t('general.loading') }}
        </p>
        <p
          v-else-if="!connectedAccounts"
          :class="$style.text"
        >
          {{ $t('account.security.social_accounts.no_account') }}
        </p>
        <p
          v-else
          :class="$style.text"
        >
          {{ $t('account.security.social_accounts.connected') }}:
          <span
            v-for="(socialAccount, i) in socialAccountsListConnected"
            :key="i"
          >
            {{ socialAccount.name }}
            {{ i === socialAccountsListConnected.length - 1 ? '' : ' | ' }}
          </span>
        </p>
      </div>
      <div
        v-else
        :class="$style.viewContent"
      >
        <p :class="$style.description">
          {{
            $t('account.security.social_accounts.desc', { appName: appName() })
          }}
        </p>

        <div
          v-for="(socialAccount, i) in socialAccountsList"
          :key="i"
          :class="$style.socialAccount"
        >
          <div class="flex items-center">
            <div class="h-8 w-8 text-2xl flex items-center justify-center">
              <NuxtIcon
                aria-hidden="true"
                filled
                :name="socialAccount.logo"
              />
            </div>
            <p :class="$style.name">{{ socialAccount.name }}</p>
          </div>
          <div v-if="!socialAccount.isAvailable">
            <button
              class="btn-outlined h-auto font-bold uppercase text-xs"
              :class="$style.btn"
              disabled
            >
              <NuxtIcon name="timer" />
              {{ $t('general.soon') }}
            </button>
          </div>
          <button
            v-else-if="!socialAccount.isConnected"
            class="btn-primary h-auto font-bold uppercase text-xs"
            :class="$style.btn"
            @click="onConnectButtonClick(socialAccount)"
          >
            <NuxtIcon name="sign_in" />
            {{ $t('account.security.social_accounts.connect') }}
          </button>
          <button
            v-else
            class="btn-outlined h-auto font-bold uppercase text-xs"
            :class="$style.btn"
            @click="onDisconnectButtonClick(socialAccount)"
          >
            <NuxtIcon name="close" />
            {{ $t('account.security.social_accounts.disconnect') }}
          </button>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-start justify-between;

    .title {
      @apply text-sm font-semibold;
    }

    .edit {
      @apply text-xs font-bold uppercase opacity-60 px-1;
    }
  }

  .viewContent {
    @apply text-base text-texts/80;

    .text {
      @apply text-xs;
    }

    .description {
      @apply text-sm md:text-sm text-texts/70 mb-4 max-w-full;
    }

    .socialAccount {
      @apply first:mt-4 py-4 flex justify-between items-center border-texts/10 border-b border-solid last:border-0;

      .name {
        @apply ml-4 text-sm font-bold;
      }

      .btn {
        @apply flex items-center gap-1.5;
      }
    }
  }
}
</style>
